import { Card, Typography, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  DELETE_PRODUCTION_DETAILS,
  EXPORT_PRODUCTION_DETAILS,
  GET_PRODUCTION_DETAILS,
  VIEW_PRODUCTION_DETAILS,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { cglFormatDate } from "utils/functions";

const ProductionDetails = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Fix round 1: the date actually queried (mount default or whatever the user last
  // submitted), for the A3 empty-state hint.
  const [queryDate, setQueryDate] = useState(moment());

  // Fix round 1: guard against setState after unmount (cold review finding 2).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Final fix wave (C1): request-sequence ref — a fast manual Preview must never be
  // overwritten by a slower, stale mount request resolving afterwards.
  const reqIdRef = useRef(0);
  const lastFilterValuesRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/production_details/")[1] : "";
    return updatedPath;
  };

  const getAllDetails = useCallback(async (filterValues) => {
    setLoading(true);
    const query = `${GET_PRODUCTION_DETAILS}`;
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };

    const response = await getData(query, false, bodyData);

    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting details data");
    }
  }, []);

  const exportDetails = async (filterValues) => {
    const {
      start_date,
      end_date,
      sales_contract_id,
      style_id,
      buying_office_id,
      buyer_id,
    } = filterValues;
    const bodyData = {
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(sales_contract_id && { sales_contract_id }),
      ...(buying_office_id && { buying_office_id }),
      ...(buyer_id && { buyer_id }),
      ...(style_id && { style_id }),
    };
    if (
      Object.values(bodyData).length &&
      !Object.values(bodyData).some((val) => val === undefined)
    ) {
      setLoading(true);

      const query = `${EXPORT_PRODUCTION_DETAILS}`;

      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = `production_details_report.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setLoading(false);
        message.success("Fabric Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    } else {
      // Round 3 fix round 1 (cold review REVIEW-T2.md, Important): this branch is what the
      // shared SearchForm's Clear button reaches. common/SearchForm/index.js's Clear handler
      // calls onFinish(null, null, filterName); the "production_details" switch case there
      // calls fetchTableData({start_date: undefined, ...}) (every field undefined), and
      // fetchTableData is this page's exportDetails via FilterComponent's filterAPI prop -- so
      // bodyData above ends up empty and the export branch is skipped, same as before this fix.
      // Before round 3 that made Clear a true no-op, which left the last-fetched table on
      // screen. Round 5 (T2) removed the hasSearched gate this reset used to also touch (the
      // table renders unconditionally again), but Clear must still clear the table itself, so
      // the tableData reset stays.
      setTableData(null);
    }
  };

  const previewFunction = async (filterValues) => {
    // Final fix wave (C1): claim this request's sequence number before the await so a
    // later, faster request (e.g. the user's own manual Preview) can invalidate this one.
    const myReq = ++reqIdRef.current;
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);

    const bodyData = {
      ...filterValues,
    };
    // Fix round 1: track the date actually queried for the A3 empty-state hint.
    if (filterValues?.start_date) {
      setQueryDate(moment(filterValues.start_date));
    }

    const query = `${VIEW_PRODUCTION_DETAILS}`;
    const response = await postData(query, bodyData);

    // Fix round 1: mounted guard (cold review finding 2).
    // Final fix wave (C1): also drop this response if a newer request has since been fired
    // (stale-response race — see FINAL-REVIEW.md).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.code === 200) {
      setLoading(false);
      setTableData(response?.data);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Round 5 (T2): restored. Round 3 removed this because the endpoint measured 14.77s warm
  // on a real data day (148.26s on a heavier one, T1-timings/timings.md row 02). Task 1's
  // set-based rewrite of the ProductionDetails trait now answers the same seeded default in
  // 1.70s warm in-process on the heaviest golden day (2026-08-18) and 1.52-1.61s on the
  // kernel-dispatch path (D:\cgl-erp\patches\ux-round5\golden\timings.md), comfortably
  // inside R5-5's 5s-warm/15s-cold bar, so the mount-time fetch is safe again. The filter
  // still seeds today's date (see
  // common/SearchForm/FilterFields/ProductionDetailsFilterFileds.js).
  useEffect(() => {
    previewFunction({ start_date: moment().format("YYYY-MM-DD") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedTracking = async (name) => {
    setLoading(true);
    const updatedName = cutPath(name);
    const query = `${DELETE_PRODUCTION_DETAILS}`;
    const bodyData = {
      name: updatedName,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setLoading(false);
      message.success("Production Details Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/production_details/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const tableColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Contract Type",
      dataIndex: "type",
      key: "type",
      width: 150,
    },
    {
      title: "Buyer/1st Party",
      dataIndex: "buyer",
      key: "buyer",
      width: 150,
    },
    {
      title: "Reference No",
      dataIndex: "sales_contract",
      key: "sales_contract",
      width: 150,
    },
    {
      title: "PO",
      dataIndex: "po",
      key: "po",
      width: 150,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      width: 150,
    },
    {
      title: "Style",
      dataIndex: "style",
      key: "style",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 150,
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      width: 150,
    },
    {
      title: "Substyle",
      dataIndex: "sub_style",
      key: "sub_style",
      width: 150,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 150,
    },
    {
      title: "O.Qty",
      dataIndex: "order_quantity",
      key: "order_quantity",
      width: 150,
    },
    {
      title: "Cutting TDY",
      dataIndex: "cut_today",
      key: "cut_today",
      width: 150,
    },
    {
      title: "Cutting UTD",
      dataIndex: "cut_uptodate",
      key: "cut_uptodate",
      width: 150,
    },
    {
      title: "Cutting WIP",
      dataIndex: "cut_balance",
      key: "cut_balance",
      width: 150,
    },
    {
      title: "Cutting Start",
      dataIndex: "cutting_start",
      key: "cutting_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Cutting End",
      dataIndex: "cutting_end",
      key: "cutting_end",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Line",
      dataIndex: "line",
      key: "line",
      width: 150,
    },
    {
      title: "Assign TDY",
      dataIndex: "assign_today",
      key: "assign_today",
      width: 150,
    },
    {
      title: "Assign UTD",
      dataIndex: "assign_uptodate",
      key: "assign_uptodate",
      width: 150,
    },
    {
      title: "Assign WIP",
      dataIndex: "assign_wip",
      key: "assign_wip",
      width: 150,
    },
    {
      title: "Assign Start",
      dataIndex: "assign_start",
      key: "assign_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Assign End",
      dataIndex: "assign_end",
      key: "assign_end",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Line",
      dataIndex: "sew_line",
      key: "sew_line",
      width: 150,
    },
    {
      title: "Sewing TDY",
      dataIndex: "sew_today",
      key: "sew_today",
      width: 150,
    },
    {
      title: "Sewing UTD",
      dataIndex: "sew_uptodate",
      key: "sew_uptodate",
      width: 150,
    },
    {
      title: "Sewing WIP",
      dataIndex: "sew_wip",
      key: "sew_wip",
      width: 150,
    },
    {
      title: "Rejected",
      dataIndex: "reject",
      key: "reject",
      width: 150,
    },
    {
      title: "Sewing Start",
      dataIndex: "sewing_start",
      key: "sewing_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Sewing End",
      dataIndex: "sewing_end",
      key: "sewing_end",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Folding TDY",
      dataIndex: "fold_today",
      key: "fold_today",
      width: 150,
    },
    {
      title: "Folding UTD",
      dataIndex: "fold_uptodate",
      key: "fold_uptodate",
      width: 150,
    },
    {
      title: "Folding WIP",
      dataIndex: "fold_wip",
      key: "fold_wip",
      width: 150,
    },
    {
      title: "Folding Start",
      dataIndex: "folding_start",
      key: "folding_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Folding End",
      dataIndex: "folding_end",
      key: "folding_end",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Packing TDY",
      dataIndex: "pack_today",
      key: "pack_today",
      width: 150,
    },
    {
      title: "Packing UTD",
      dataIndex: "pack_uptodate",
      key: "pack_uptodate",
      width: 150,
    },
    {
      title: "Packing WIP",
      dataIndex: "pack_wip",
      key: "pack_wip",
      width: 150,
    },
    {
      title: "Packing Start",
      dataIndex: "packing_start",
      key: "packing_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Packing End",
      dataIndex: "packing_end",
      key: "packing_end",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Ex-Factory Qty",
      dataIndex: "ex_factory_quantity",
      key: "ex_factory_quantity",
      width: 150,
    },
    {
      title: "Ex-factory WIP",
      dataIndex: "ex_short",
      key: "ex_short",
      width: 150,
    },
    {
      title: "Ex-Factory Date",
      dataIndex: "ex_factory_start",
      key: "ex_factory_start",
      width: 150,
      render: (value) => cglFormatDate(value),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportDetails}
        filterName={"production_details"}
        searchButtonText={"Export"}
        defaultOpen
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportDetails}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Production Details</Title>}
          />
        }
        content={
          <>
            <div className="containt-body">
              <Card>{memoizedCustomFilter}</Card>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => previewFunction(lastFilterValuesRef.current)}
                dataSource={tableData}
                columns={tableColumns.map((col, idx) => ({
                  ...col,
                  fixed: idx < 5 ? "left" : undefined,
                }))}
                pagination
                scroll={{
                  x: 1200,
                  y: 500,
                }}
                bordered
                sticky
                locale={{
                  emptyText: `No records for ${queryDate.format(
                    "DD-MMM-YYYY",
                  )}. Change the date above.`,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default ProductionDetails;
